import router from 'next/router';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { SearchDropdownProps } from './type';
import SearchIcon from 'icons/SearchIcon';
import { AdminRoutes, ContractorRoutes } from 'routes';
import { EmployeeType } from 'lib/constants';
import { getContractTypeLabel } from 'utils/app';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const SearchDropdown = React.forwardRef<any, SearchDropdownProps>(
  (
    {
      filteredActionValues, // list of action items such as Pages routes
      filteredPeopleValues, // list of people items such as Employees and Contractors (Manager PoV)
      filteredContractValues, // list of contract items such as Contracts (Worker PoV)
      handleInputChange, // function to handle input change
      name, // name of the input
      onSelectedItemChange, // function to handle selected item change
      selectedItem, // selected item
      onEnterPress, // function to handle enter press
      placeholderText = 'Search for anything...',
      isNavbar = false,
      onOpenCommandPalette,
    },
    ref,
  ) => {
    const [isMac, setIsMac] = useState(true);
    useEffect(() => {
      setIsMac(/Mac|iPhone|iPad|iPod/.test(navigator.platform) || /Mac/.test(navigator.userAgent));
    }, []);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const attachInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const hasQuery = selectedItem !== '';
    const showCommandPaletteHint = Boolean(onOpenCommandPalette) && !hasQuery;
    const hasResults =
      filteredActionValues.length > 0 || filteredPeopleValues.length > 0 || filteredContractValues.length > 0;

    const handleInputPress = (value: string) => {
      handleInputChange(value);
    };

    const handleClear = () => {
      handleInputChange('');
      // Headless UI leaves the input uncontrolled and syncs it imperatively, so clear the DOM
      // node directly instead of waiting on its internal sync to notice the empty value.
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    };

    const handleOptionClick = (value: string) => {
      if (typeof value !== 'string') {
        // Handle the case when value is null or undefined (e.g., when clearing the input)
        onSelectedItemChange(null);
        return;
      }
      // Check if the selected value is an action item
      const actionItem = filteredActionValues.find((item) => item.helperLabel === value);
      if (actionItem) {
        router.push(actionItem.href);
        onSelectedItemChange(null);
        return;
      }

      const [email, employmentType] = value?.split('-');
      const selectedPerson = filteredPeopleValues.find(
        (person) => person.user.email === email && person.employmentType === employmentType,
      );

      if (selectedPerson) {
        onSelectedItemChange(selectedPerson);
        setTimeout(() => {
          onEnterPress(email, selectedPerson.employmentType as EmployeeType);
        }, 0);
      }
      // Check if it's a contract item
      const selectedContract = filteredContractValues.find(
        (contract) => contract.contract.name === value || contract.company.companyName === value,
      );
      if (selectedContract) {
        onSelectedItemChange(selectedContract);
        const contractName = selectedContract.contract?.name || selectedContract.company.companyName;
        onEnterPress(contractName);
        return;
      }

      // If we get here, no matching item was found
      onSelectedItemChange(null);
    };

    return (
      <Combobox as="div" value={selectedItem} onChange={handleOptionClick} name={name} className="z-10" immediate>
        <div className="relative w-72">
          {/* Also the reopen affordance: Escape closes the panel without blurring, and the input
              alone has no handler that reopens it on click. */}
          <ComboboxButton
            aria-label="Show search results"
            className="absolute left-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center focus:outline-none"
          >
            <SearchIcon />
          </ComboboxButton>
          <ComboboxInput
            id="Search"
            aria-label="Search"
            className={classNames(
              'focus:shadow-outline-neutral block w-full rounded-lg border-none py-2 pl-9 leading-5 transition duration-150 ease-in-out focus:border-neutral-300 focus:outline-none md:text-sm',
              hasQuery ? 'pr-10' : showCommandPaletteHint ? 'pr-14' : 'pr-3',
              isNavbar
                ? 'bg-neutral-100 placeholder:text-neutral-600 focus:placeholder:text-neutral-600'
                : 'bg-white placeholder:text-neutral-400 focus:placeholder:text-neutral-500',
            )}
            onChange={(event) => {
              handleInputPress(event.target.value);
            }}
            displayValue={(value: string) => {
              return value ?? '';
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const selectedPerson = filteredPeopleValues.find(
                  (person) => person.user.email.toLowerCase() === (selectedItem ?? '').toLowerCase(),
                );
                if (selectedPerson) {
                  onEnterPress(selectedPerson.user.email, selectedPerson.employmentType);
                }
              }

              event.stopPropagation();
            }}
            name={name}
            ref={attachInputRef}
            placeholder={placeholderText}
            autoComplete="off" // disable autocomplete
          />
          {hasQuery && (
            <button
              type="button"
              aria-label="Clear search"
              // Keep focus in the input so clearing doesn't blur and collapse the dropdown.
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleClear}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-therablue"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {showCommandPaletteHint && (
            <button
              type="button"
              onClick={onOpenCommandPalette}
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded border border-neutral-300 bg-neutral-200/60 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 hover:bg-neutral-200"
              tabIndex={-1}
            >
              {isMac ? '⌘K' : 'Ctrl+K'}
            </button>
          )}

          {hasQuery && (
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-neutral-100 text-base shadow-lg ring-black ring-opacity-5 focus:outline-none md:text-sm">
              {!hasResults && (
                <ComboboxOption
                  key={'noResultsFound'}
                  value={'No results found'}
                  className={() =>
                    classNames(
                      'relative h-20 cursor-default select-none bg-white py-2 text-gray-900 hover:cursor-pointer',
                    )
                  }
                  disabled={true}
                >
                  <div className="mt-2 flex flex-col items-center justify-center">
                    <span
                      className={classNames('truncate text-base font-medium text-neutral-900')}
                    >{`No results found`}</span>
                    <span className={classNames('mt-1 truncate text-sm font-normal text-neutral-500')}>
                      {`Try searching again`}
                    </span>
                  </div>
                </ComboboxOption>
              )}
              {filteredActionValues.length > 0 && (
                <>
                  <ComboboxOption
                    key={'ACTIONS'}
                    value={'ACTIONS'}
                    className={() =>
                      classNames(
                        'relative cursor-default select-none py-3 pl-3 text-xs font-medium leading-tight text-neutral-400 hover:cursor-pointer',
                      )
                    }
                    disabled={true}
                  >
                    <div className="flex">
                      <span className={classNames('truncate')}>ACTIONS</span>
                    </div>
                  </ComboboxOption>
                  {filteredActionValues.map((value, index) => (
                    <ComboboxOption
                      key={index}
                      value={value.helperLabel}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-default select-none bg-white py-2 pl-3 pr-9 hover:cursor-pointer',
                          active ? '!bg-therablue text-white' : 'text-gray-900',
                        )
                      }
                      onClick={() => {
                        router.push(value.href);
                      }}
                    >
                      {({ active, selected }) => (
                        <>
                          <div className="flex">
                            <span className={classNames('truncate', selected && 'font-semibold')}>
                              {value.helperLabel}
                            </span>
                          </div>

                          {selected && (
                            <span
                              className={classNames(
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                active ? 'text-white' : 'text-blue-600',
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </ComboboxOption>
                  ))}
                </>
              )}
              {filteredPeopleValues.length > 0 && (
                <>
                  <ComboboxOption
                    key={'PEOPLE'}
                    value={'PEOPLE'}
                    className={() =>
                      classNames(
                        'relative cursor-default select-none py-3 pl-3 text-xs font-medium leading-tight text-neutral-400 hover:cursor-pointer',
                      )
                    }
                    disabled={true}
                  >
                    <div className="flex">
                      <span className={classNames('truncate')}>PEOPLE</span>
                    </div>
                  </ComboboxOption>
                  {filteredPeopleValues.map((value, index) => (
                    <ComboboxOption
                      key={`${index}-${value.employmentType}-${value.user.email}`}
                      value={`${value.user.email}-${value.employmentType}`}
                      className={({ active }) =>
                        classNames(
                          'relative h-12 cursor-default select-none bg-white py-2 pl-3 pr-9 hover:cursor-pointer',
                          active ? '!bg-therablue text-white' : 'text-gray-900',
                        )
                      }
                      onClick={() => {
                        const isW2Employee = Boolean(value.employments?.[0].w2Payroll?.checkEmployeeId);
                        const isContractor = value.employments?.[0].type === EmployeeType.CONTRACTOR;
                        const isEOREmployee = value.employments?.[0].type === EmployeeType.EMPLOYEE;
                        if (isContractor) {
                          router.push(`${AdminRoutes.ContractorProfile}/${value.user.userID}`);
                        } else if (isEOREmployee) {
                          router.push(`${AdminRoutes.EOREmployeeProfile}/${value.user.userID}`);
                        } else if (isW2Employee) {
                          router.push(
                            `${AdminRoutes.EmployeeProfile}/${value.employments?.[0].w2Payroll?.checkEmployeeId}`,
                          );
                        }
                      }}
                    >
                      {({ active, selected }) => (
                        <>
                          <div className="flex flex-col">
                            <span className={classNames('truncate', selected && 'font-semibold')}>
                              {`${value.user.firstName} ${value.user.lastName}`} [
                              {`${getContractTypeLabel(value.employmentType)}`}]
                            </span>
                            <span
                              className={classNames(
                                'truncate text-xs font-normal',
                                active ? 'text-white' : 'text-neutral-500',
                              )}
                            >
                              {value.user.email}
                            </span>
                          </div>

                          {selected && (
                            <span
                              className={classNames(
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                active ? 'text-white' : 'text-blue-600',
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </ComboboxOption>
                  ))}
                </>
              )}
              {filteredContractValues.length > 0 && (
                <>
                  <ComboboxOption
                    key={'CONTRACTS'}
                    value={'CONTRACTS'}
                    className={() =>
                      classNames(
                        'relative cursor-default select-none py-3 pl-3 text-xs font-medium leading-tight text-neutral-400 hover:cursor-pointer',
                      )
                    }
                    disabled={true}
                  >
                    <div className="flex">
                      <span className={classNames('truncate')}>CONTRACTS</span>
                    </div>
                  </ComboboxOption>
                  {filteredContractValues.map((value, index) => (
                    <ComboboxOption
                      key={`${index}-${value.contract.id}`}
                      value={value?.contract.name}
                      className={({ active }) =>
                        classNames(
                          'relative cursor-default select-none bg-white py-2 pl-3 pr-9 hover:cursor-pointer',
                          active ? '!bg-therablue text-white' : 'text-gray-900',
                        )
                      }
                      onClick={() => {
                        const contract = value.contract.type === EmployeeType.CONTRACTOR && value?.contract.id;
                        const checkCompanyID = value?.company?.w2Payroll?.checkId;
                        if (contract) {
                          router.push(ContractorRoutes?.ContractDetails.replace('[contractID]', value?.contract.id));
                        } else router.push(ContractorRoutes?.EmployerProfile.replace('[companyID]', checkCompanyID));
                      }}
                    >
                      {({ active, selected }) => (
                        <>
                          <div className="flex">
                            <span className={classNames('truncate', selected && 'font-semibold')}>
                              {value.contract.name}
                            </span>
                          </div>

                          {selected && (
                            <span
                              className={classNames(
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                active ? 'text-white' : 'text-blue-600',
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </ComboboxOption>
                  ))}
                </>
              )}
            </ComboboxOptions>
          )}
        </div>
      </Combobox>
    );
  },
);

export default SearchDropdown;
